/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from "../../services/Axios/axios";
import RenderLoanPage from "../RenderLoanPage";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function SettlementDetailed() {
  const history = useHistory();
  const query = useQuery();
  const id = query.get("id");

  const [settlement, setSettlement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [viewing, setViewing] = useState(false);

  const [form, setForm] = useState({
    amount: "",
    last_expected_payment_date: "",
    loan_officer_name: "",
    loan_id: "",
  });
  const [formError, setFormError] = useState({});

  const adminUser = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    if (adminUser?.name) {
      setForm((prev) => ({ ...prev, loan_officer_name: adminUser.name }));
    }
  }, []);

  const fetchSettlement = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`/settlement/${id}`);
      setSettlement(res.data);
    } catch (err) {
      toast.error("Failed to load settlement details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettlement();
  }, [id]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError({ ...formError, [e.target.name]: "" });
  };

  const validate = () => {
    const err = {};
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      err.amount = "Please enter a valid amount";
    if (!form.loan_id)
      err.loan_id = "Loan ID is required";
    if (!form.last_expected_payment_date)
      err.last_expected_payment_date = "Last expected payment date is required";
    if (!form.loan_officer_name)
      err.loan_officer_name = "Loan officer name is required";
    setFormError(err);
    return Object.keys(err).length === 0;
  };

  const handleProcess = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setProcessing(true);
    try {
      const res = await axiosInstance.put(`/settlement/process/${id}`, {
        amount: Number(form.amount),
        last_expected_payment_date: form.last_expected_payment_date,
        loan_officer_name: form.loan_officer_name,
        loan_id: form.loan_id || undefined,
      });
      setSettlement(res.data);
      toast.success("Settlement processed and sent to member!");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to process settlement."
      );
    } finally {
      setProcessing(false);
    }
  };

  const fetchPdfBlob = async () => {
    const res = await axiosInstance.get(`/settlement/pdf/${id}`, {
      responseType: "blob",
    });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const blob = await fetchPdfBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `settlement-quote-${settlement?.member_name || id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const handleViewPDF = async () => {
    setViewing(true);
    try {
      const blob = await fetchPdfBlob();
      const url = window.URL.createObjectURL(blob);
      if (pdfBlobUrl) window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(url);
    } catch (err) {
      toast.error("Failed to load PDF.");
    } finally {
      setViewing(false);
    }
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    marginBottom: 6,
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: 6,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  };

  const errorStyle = {
    color: "#e53935",
    fontSize: 12,
    marginTop: 4,
    display: "block",
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: "#fff3e0", color: "#e65100", border: "#ffb74d" },
      processed: { bg: "#e8f5e9", color: "#2e7d32", border: "#81c784" },
    };
    const s = map[status] || map.pending;
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
          padding: "4px 12px",
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 600,
          textTransform: "capitalize",
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <RenderLoanPage title="Settlement Request Detail">
      <ToastContainer />

      <button
        onClick={() => history.goBack()}
        style={{
          background: "none",
          border: "1px solid #ccc",
          borderRadius: 6,
          padding: "6px 14px",
          cursor: "pointer",
          marginBottom: 20,
          fontSize: 13,
          color: "#555",
        }}
      >
        ← Back
      </button>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          Loading...
        </div>
      ) : !settlement ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          Settlement not found.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {/* Left: Member Info */}
          <div
            style={{
              flex: 1,
              minWidth: 280,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#2d6a4f",
                padding: "18px 22px",
                color: "#fff",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                Member Information
              </h3>
            </div>
            <div style={{ padding: "20px 22px" }}>
              {[
                ["Member Name", settlement.member_name],
                ["Staff ID", settlement.staff_id],
                ["Email", settlement.email],
                ["Phone", settlement.phone],
                ["ID Card", settlement.id_card],
                ["Loan ID", settlement.loan_id || "—"],
                ["Status", statusBadge(settlement.status)],
                [
                  "Submitted",
                  settlement.created_at
                    ? new Date(settlement.created_at).toLocaleDateString("en-GB")
                    : "—",
                ],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "9px 0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 600 }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 13, color: "#333", textAlign: "right" }}>
                    {value}
                  </span>
                </div>
              ))}

              {/* Processed info */}
              {settlement.status === "processed" && (
                <>
                  {[
                    [
                      "Amount (GHS)",
                      Number(settlement.amount).toLocaleString("en-GH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }),
                    ],
                    [
                      "Issue Date",
                      settlement.issue_date
                        ? new Date(settlement.issue_date).toLocaleDateString("en-GB")
                        : "—",
                    ],
                    [
                      "Last Payment Date",
                      settlement.last_expected_payment_date
                        ? new Date(
                            settlement.last_expected_payment_date
                          ).toLocaleDateString("en-GB")
                        : "—",
                    ],
                    ["Loan Officer", settlement.loan_officer_name],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "9px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <span style={{ fontSize: 13, color: "#888", fontWeight: 600 }}>
                        {label}
                      </span>
                      <span style={{ fontSize: 13, color: "#333" }}>{value}</span>
                    </div>
                  ))}

                  <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                    <button
                      onClick={handleViewPDF}
                      disabled={viewing}
                      style={{
                        flex: 1,
                        padding: "11px",
                        background: viewing ? "#aaa" : "#2d6a4f",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: viewing ? "not-allowed" : "pointer",
                      }}
                    >
                      {viewing ? "Loading..." : "View PDF"}
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      disabled={downloading}
                      style={{
                        flex: 1,
                        padding: "11px",
                        background: downloading ? "#aaa" : "#1565c0",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: downloading ? "not-allowed" : "pointer",
                      }}
                    >
                      {downloading ? "Downloading..." : "Download PDF"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: Process Form (only shown when pending) or PDF Preview */}
          {settlement.status === "pending" && (
            <div
              style={{
                flex: 1,
                minWidth: 280,
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#1565c0",
                  padding: "18px 22px",
                  color: "#fff",
                }}
              >
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  Process Settlement Quote
                </h3>
                <p style={{ margin: "5px 0 0", fontSize: 12, opacity: 0.85 }}>
                  Fill in the settlement details. Issue date is set automatically.
                </p>
              </div>
              <div style={{ padding: "20px 22px" }}>
                {/* Issue Date (auto) */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Issue Date (auto)</label>
                  <input
                    style={{ ...inputStyle, background: "#f5f5f5", color: "#666" }}
                    value={new Date().toLocaleDateString("en-GB")}
                    readOnly
                  />
                </div>

                {/* Amount */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Settlement Amount (GHS) *</label>
                  <input
                    style={inputStyle}
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="e.g. 3811.06"
                    step="0.01"
                    min="0"
                  />
                  {formError.amount && (
                    <span style={errorStyle}>{formError.amount}</span>
                  )}
                </div>

                {/* Loan ID */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Loan ID *</label>
                  <input
                    style={inputStyle}
                    type="text"
                    name="loan_id"
                    value={form.loan_id}
                    onChange={handleChange}
                    placeholder="e.g. UPNMG/2023/2283"
                  />
                  {formError.loan_id && (
                    <span style={errorStyle}>{formError.loan_id}</span>
                  )}
                </div>

                {/* Last Expected Payment Date */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Last Expected Date for Payment *</label>
                  <input
                    style={inputStyle}
                    type="date"
                    name="last_expected_payment_date"
                    value={form.last_expected_payment_date}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                  />
                  {formError.last_expected_payment_date && (
                    <span style={errorStyle}>
                      {formError.last_expected_payment_date}
                    </span>
                  )}
                </div>

                {/* Loan Officer Name */}
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Loan Officer Name</label>
                  <input
                    style={{ ...inputStyle, background: "#f5f5f5", color: "#444", cursor: "not-allowed" }}
                    type="text"
                    value={form.loan_officer_name}
                    readOnly
                  />
                </div>

                <button
                  onClick={handleProcess}
                  disabled={processing}
                  style={{
                    width: "100%",
                    padding: "13px",
                    background: processing ? "#aaa" : "#2d6a4f",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: processing ? "not-allowed" : "pointer",
                  }}
                >
                  {processing ? "Processing..." : "Process & Send to Member"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Inline PDF Preview */}
        {pdfBlobUrl && (
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#2d6a4f",
                padding: "14px 22px",
                color: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                Settlement PDF Preview
              </span>
              <button
                onClick={() => {
                  window.URL.revokeObjectURL(pdfBlobUrl);
                  setPdfBlobUrl(null);
                }}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "#fff",
                  borderRadius: 6,
                  padding: "4px 12px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
            <iframe
              src={pdfBlobUrl}
              title="Settlement PDF"
              style={{ width: "100%", height: 700, border: "none" }}
            />
          </div>
        )}
        </div>
      )}
    </RenderLoanPage>
  );
}

export default SettlementDetailed;
